import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FileText, History, Trash2, Upload } from "lucide-react"
import { type ChangeEvent, useRef, useState } from "react"

import { api, ErrorApi, pedir } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

import { type Archivo, kilobytes } from "./tipos"

/**
 * Los archivos de una carpeta, con su historial.
 *
 * ⚠ **Subir un nombre que ya existe NO reemplaza: añade una versión.** La pantalla
 * lo dice antes de que ocurra, porque el gesto —elegir un archivo que se llama igual
 * y darle a subir— es exactamente el mismo que en una carpeta compartida, donde sí
 * pisa. Quien no lo sepa creerá haber perdido lo anterior.
 *
 * ⚠ **La descarga va por el endpoint autenticado.** `storage/` no se sirve como
 * estático nunca (regla 8): comprueba sesión, permiso de la carpeta y contención de
 * ruta antes de leer un byte.
 */

/** Lo que la lista blanca del servidor admite. Es comodidad, NO la defensa. */
const ACEPTA = ".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.docx,.pptx,.csv,.txt"

export function PanelArchivos({ carpetaId, nombre }: { carpetaId: number; nombre: string }) {
  const entrada = useRef<HTMLInputElement>(null)
  const [historial, setHistorial] = useState<number | null>(null)
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const puedeSubir = tienePermiso(usuario, "drive:subir")
  const vista = useRef<HTMLDivElement | null>(null)

  const archivos = useQuery({
    queryKey: ["drive", "archivos", carpetaId],
    queryFn: () => api.get<Archivo[]>(`/api/v1/drive/carpetas/${carpetaId}/archivos`),
  })

  const invalidar = () =>
    clienteQuery.invalidateQueries({ queryKey: ["drive", "archivos", carpetaId] })

  const subir = useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData()
      cuerpo.append("archivo", archivo)
      // `FormData` y no `api.post`: el navegador tiene que poner su propio
      // `Content-Type` con el `boundary`; escribirlo a mano lo rompe.
      return pedir<Archivo>(`/api/v1/drive/carpetas/${carpetaId}/archivos`, {
        method: "POST",
        body: cuerpo,
      })
    },
    onSuccess: () => {
      invalidar()
      if (entrada.current) entrada.current.value = ""
    },
  })

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/drive/archivos/${id}`),
    onSuccess: invalidar,
  })

  function alElegir(evento: ChangeEvent<HTMLInputElement>) {
    const archivo = evento.target.files?.[0]
    if (archivo) subir.mutate(archivo)
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold">{nombre}</h2>
        <div className="flex flex-wrap items-start gap-2">
          {!!archivos.data?.length && (
            <Exportar
              titulo={`Archivos de ${nombre}`}
              subtitulo="Drive de la empresa · versión vigente de cada archivo"
              contenido={vista}
            />
          )}
          {puedeSubir && (
            <label
              className="flex h-9 cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border px-3 text-sm"
              style={{ borderColor: "var(--border)" }}
            >
              <Upload size={16} aria-hidden="true" />
              {subir.isPending ? "Subiendo…" : "Subir archivo"}
              <input
                ref={entrada}
                type="file"
                className="sr-only"
                accept={ACEPTA}
                onChange={alElegir}
                disabled={subir.isPending}
              />
            </label>
          )}
        </div>
      </div>

      {puedeSubir && (
        <p className="mb-4 text-xs" style={{ color: "var(--fg-muted)" }}>
          Subir un archivo con un nombre que ya está aquí guarda una versión nueva; la anterior se
          conserva y se puede volver a bajar. Hasta 50 MB. No se admiten comprimidos ni ejecutables.
        </p>
      )}

      {subir.isError && (
        <p role="alert" className="mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          {subir.error instanceof ErrorApi ? subir.error.message : "No se pudo subir el archivo"}
        </p>
      )}

      {archivos.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}

      {archivos.data?.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 superficie py-10"
          style={{ color: "var(--fg-muted)" }}
        >
          <FileText size={22} aria-hidden="true" />
          <p className="text-sm">Esta carpeta está vacía</p>
        </div>
      )}

      {archivos.data && archivos.data.length > 0 && (
        <div ref={vista}>
          <Tabla etiqueta={`Archivos de ${nombre}`}>
            <Cabecera>
              <Th>Archivo</Th>
              <Th derecha>Versión</Th>
              <Th derecha>Tamaño</Th>
              <Th derecha>Actualizado</Th>
              <Th />
            </Cabecera>
            <Cuerpo>
              {archivos.data.map((archivo) => {
                const actual = archivo.versiones[0]
                const abierto = historial === archivo.id
                return (
                  <Fila key={archivo.id}>
                    <Td data-exportar-valor={archivo.nombre}>
                      <a
                        href={`/api/v1/drive/archivos/${archivo.id}/contenido`}
                        className="underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {archivo.nombre}
                      </a>
                      {abierto && archivo.versiones.length > 1 && (
                        <ul className="mt-2 space-y-1">
                          {archivo.versiones.slice(1).map((v) => (
                            <li key={v.id} className="text-xs">
                              <a
                                href={`/api/v1/drive/archivos/${archivo.id}/contenido?version=${v.numero}`}
                                className="underline"
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: "var(--fg-muted)" }}
                              >
                                Versión {v.numero} · {comoFechaHora(v.creado_en)} ·{" "}
                                {kilobytes(v.bytes)}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Td>
                    <Td derecha numero data-exportar-valor={String(actual?.numero ?? 1)}>
                      {archivo.versiones.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => setHistorial(abierto ? null : archivo.id)}
                          aria-expanded={abierto}
                          className="inline-flex items-center gap-1"
                        >
                          <History size={13} aria-hidden="true" />
                          <Insignia tono="info">v{actual?.numero ?? 1}</Insignia>
                        </button>
                      ) : (
                        <span>v{actual?.numero ?? 1}</span>
                      )}
                    </Td>
                    <Td
                      derecha
                      numero
                      data-exportar-valor={
                        actual ? `${(actual.bytes / 1024).toFixed(1).replace(".", ",")} KB` : "—"
                      }
                    >
                      {actual ? kilobytes(actual.bytes) : "—"}
                    </Td>
                    <Td derecha numero style={{ color: "var(--fg-muted)" }}>
                      {actual ? comoFechaHora(actual.creado_en) : "—"}
                    </Td>
                    <Td derecha>
                      {puedeSubir && (
                        <button
                          type="button"
                          onClick={() => eliminar.mutate(archivo.id)}
                          disabled={eliminar.isPending}
                          aria-label={`Retirar ${archivo.nombre}`}
                          className="rounded-[var(--radius-control)] p-1.5 disabled:opacity-40"
                          style={{ color: "var(--destructive)" }}
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      )}
                    </Td>
                  </Fila>
                )
              })}
            </Cuerpo>
          </Tabla>
        </div>
      )}
    </div>
  )
}
